import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import type { Geometry } from 'geojson';
import type { ArcgisMapView } from './arcgisView';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';
import { ActionPopupContent } from 'components/IAP/ActionPopupContent';
import {
  getActionCoordinates,
  getActionGeoJsonGeometry,
} from 'components/IAP/utils/actionGeolocation';
import { getActionMapId } from 'components/IAP/utils/actionMapId';
import { formatActionTask } from 'components/IAP/utils/actionTask';
import {
  ACTION_SYMBOL_KEY_FIELD,
  actionSymbolFacets,
  actionSymbolKey,
  type ActionSymbolFacets,
} from 'components/IAP/utils/actionSymbology';
import {
  actionClusterReduction,
  actionPointRenderer,
  polygonSymbolForAction,
} from './actionLayerGraphics';
import { isUscgSymbol } from './uscgSymbology';
import { WGS84_SPATIAL_REFERENCE } from './spatialReference';
import { match, P } from 'ts-pattern';

export const INCIDENT_ACTIONS_LAYER_ID = 'incident-actions-layer';
export const INCIDENT_ACTION_AREAS_LAYER_ID = 'incident-action-areas-layer';

const OBJECT_ID_FIELD = 'objectId';

function geoJsonToArcgisGeometry(
  geometry: Geometry
): __esri.Point | __esri.Polygon | null {
  return match(geometry)
    .with({ type: 'Point' }, (g) => {
      const [longitude, latitude] = g.coordinates;
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude))
        return null;
      return new Point({ longitude, latitude });
    })
    .with({ type: 'Polygon' }, (g) => new Polygon({ rings: g.coordinates }))
    .with(
      { type: 'MultiPolygon' },
      (g) => new Polygon({ rings: g.coordinates.flatMap((c) => c) })
    )
    .otherwise(() => null);
}

function geometryForAction(
  action: ActionEntity
): __esri.Point | __esri.Polygon | null {
  const geoJson = getActionGeoJsonGeometry(
    (action as { geolocation?: unknown }).geolocation
  );
  if (geoJson) {
    const fromFeature = geoJsonToArcgisGeometry(geoJson);
    if (fromFeature) return fromFeature;
  }

  const coords = getActionCoordinates(
    (action as { geolocation?: unknown }).geolocation
  );
  if (!coords) return null;
  return new Point({
    longitude: coords.longitude,
    latitude: coords.latitude,
  });
}

/**
 * What a graphic would have to be redrawn for.
 *
 * Comparing this instead of rebuilding the whole layer is the difference
 * between every inline status edit clearing and re-adding a few hundred
 * graphics — which flickers and loses the open popup — and it touching one.
 */
function graphicSignature(
  action: ActionEntity,
  geometry: __esri.Point | __esri.Polygon,
  symbolKey: string
): string {
  const shape =
    geometry.type === 'point'
      ? `p:${geometry.longitude},${geometry.latitude}`
      : `a:${JSON.stringify((geometry as __esri.Polygon).rings)}`;
  return [
    shape,
    symbolKey,
    formatActionTask(action.name, action.description),
  ].join('~');
}

type SyncedAction = {
  action: ActionEntity;
  actionId: string;
  geometry: __esri.Point | __esri.Polygon;
  facets: ActionSymbolFacets;
  symbolKey: string;
  signature: string;
};

type PointEntry = { objectId: number; signature: string };

export const useActionLayerSync = ({
  mapView,
  actions,
  hiddenActionIds,
  visibleActionIds,
  selectedActionIds,
  onActionGraphicClick,
  suppressMapClicks = false,
}: {
  mapView: ArcgisMapView | null;
  actions: ActionEntity[];
  hiddenActionIds: string[];
  /** Ids the table is currently showing; null means the table is not filtering. */
  visibleActionIds?: string[] | null;
  /** Ids selected in the table, highlighted on the map. */
  selectedActionIds?: string[];
  /** Clicking a graphic tells the table which row to scroll to. */
  onActionGraphicClick?: (actionId: string) => void;
  /** When the AI select tool is on, leave the click for layer/feature pick. */
  suppressMapClicks?: boolean;
}) => {
  const pointLayerRef = useRef<FeatureLayer | null>(null);
  const areaLayerRef = useRef<GraphicsLayer | null>(null);
  const popupRootRef = useRef<Root | null>(null);
  /** ActionId -> the feature currently on the point layer. */
  const pointEntriesRef = useRef<Map<string, PointEntry>>(new Map());
  /** ActionId -> the graphic currently on the area layer. */
  const areaGraphicsRef = useRef<Map<string, Graphic>>(new Map());
  const nextObjectIdRef = useRef(1);
  const highlightRef = useRef<__esri.Handle | null>(null);

  useEffect(() => {
    if (!mapView?.map) return;

    const existingPointLayer = mapView.map.findLayerById(
      INCIDENT_ACTIONS_LAYER_ID
    ) as FeatureLayer | null;

    match(existingPointLayer)
      .with(P.nonNullable, (layer) => {
        pointLayerRef.current = layer;
      })
      .otherwise(() => {
        // A client-side FeatureLayer rather than a GraphicsLayer, because
        // clustering is a FeatureLayer feature and a district's worth of tasks
        // is unreadable without it.
        const layer = new FeatureLayer({
          id: INCIDENT_ACTIONS_LAYER_ID,
          title: 'Actions',
          geometryType: 'point',
          spatialReference: WGS84_SPATIAL_REFERENCE,
          objectIdField: OBJECT_ID_FIELD,
          source: [],
          fields: [
            { name: OBJECT_ID_FIELD, type: 'oid' },
            { name: 'actionId', type: 'string' },
            { name: 'name', type: 'string' },
            { name: ACTION_SYMBOL_KEY_FIELD, type: 'string' },
          ],
          renderer: actionPointRenderer(),
          featureReduction: actionClusterReduction(),
        });
        mapView.map.add(layer);
        pointLayerRef.current = layer;
      });

    const existingAreaLayer = mapView.map.findLayerById(
      INCIDENT_ACTION_AREAS_LAYER_ID
    ) as GraphicsLayer | null;

    match(existingAreaLayer)
      .with(P.nonNullable, (layer) => {
        areaLayerRef.current = layer;
      })
      .otherwise(() => {
        const layer = new GraphicsLayer({
          id: INCIDENT_ACTION_AREAS_LAYER_ID,
          title: 'Action Areas',
        });
        mapView.map.add(layer);
        areaLayerRef.current = layer;
      });

    return () => {
      if (popupRootRef.current) {
        popupRootRef.current.unmount();
        popupRootRef.current = null;
      }
      highlightRef.current?.remove();
      highlightRef.current = null;
    };
  }, [mapView]);

  const synced = useMemo<SyncedAction[]>(() => {
    const hidden = new Set(hiddenActionIds);
    const allowed = visibleActionIds ? new Set(visibleActionIds) : null;
    const now = new Date();

    return actions.flatMap((action) => {
      if (action.archived) return [];
      const actionId = getActionMapId(action);
      if (hidden.has(actionId)) return [];
      if (allowed && !allowed.has(actionId)) return [];

      const geometry = geometryForAction(action);
      if (!geometry) return [];

      // Validated here rather than in `actionSymbolFacets`, which stays free of
      // ArcGIS. A key the catalog no longer knows falls back to the derived
      // circle instead of rendering nothing.
      const stored = (action as { symbology?: string | null }).symbology;
      const symbology = isUscgSymbol(stored) ? stored : null;
      const facets = actionSymbolFacets(action, now, symbology);
      const symbolKey = actionSymbolKey(facets);
      return [
        {
          action,
          actionId,
          geometry,
          facets,
          symbolKey,
          signature: graphicSignature(action, geometry, symbolKey),
        },
      ];
    });
  }, [actions, hiddenActionIds, visibleActionIds]);

  /**
   * The renderer has to be rebuilt whenever a new chosen symbol appears, since
   * a `UniqueValueRenderer` only draws the values it was given. Keyed on the
   * distinct symbol keys rather than on `synced`, so moving or renaming a task
   * does not rebuild it.
   */
  const symbolKeySignature = useMemo(
    () =>
      [
        ...new Set(
          synced.filter((i) => i.facets.symbology).map((i) => i.symbolKey)
        ),
      ]
        .sort()
        .join('~'),
    [synced]
  );

  useEffect(() => {
    const layer = pointLayerRef.current;
    if (!layer) return;
    layer.renderer = actionPointRenderer(synced.map((item) => item.facets));
    // `synced` is intentionally not a dependency: the renderer only depends on
    // which symbols are in use, which is what the signature captures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolKeySignature]);

  /** Points: reconciled through applyEdits so untouched features stay put. */
  useEffect(() => {
    const layer = pointLayerRef.current;
    if (!layer) return;

    const entries = pointEntriesRef.current;
    const wanted = synced.filter((item) => item.geometry.type === 'point');
    const wantedIds = new Set(wanted.map((item) => item.actionId));

    const addFeatures: Graphic[] = [];
    const updateFeatures: Graphic[] = [];
    const deleteFeatures: { objectId: number }[] = [];

    wanted.forEach((item) => {
      const existing = entries.get(item.actionId);
      if (existing?.signature === item.signature) return;

      const objectId = existing?.objectId ?? nextObjectIdRef.current++;
      const graphic = new Graphic({
        geometry: item.geometry,
        attributes: {
          [OBJECT_ID_FIELD]: objectId,
          actionId: item.actionId,
          name: formatActionTask(item.action.name, item.action.description),
          [ACTION_SYMBOL_KEY_FIELD]: item.symbolKey,
        },
      });

      if (existing) {
        updateFeatures.push(graphic);
      } else {
        addFeatures.push(graphic);
      }
      entries.set(item.actionId, { objectId, signature: item.signature });
    });

    entries.forEach((entry, actionId) => {
      if (wantedIds.has(actionId)) return;
      deleteFeatures.push({ objectId: entry.objectId });
      entries.delete(actionId);
    });

    if (
      addFeatures.length === 0 &&
      updateFeatures.length === 0 &&
      deleteFeatures.length === 0
    ) {
      return;
    }

    void layer.applyEdits({ addFeatures, updateFeatures, deleteFeatures });
  }, [synced]);

  /** Areas: a GraphicsLayer, since clustering a polygon means nothing. */
  useEffect(() => {
    const layer = areaLayerRef.current;
    if (!layer) return;

    const graphics = areaGraphicsRef.current;
    const wanted = synced.filter((item) => item.geometry.type === 'polygon');
    const wantedIds = new Set(wanted.map((item) => item.actionId));
    const now = new Date();

    wanted.forEach((item) => {
      const existing = graphics.get(item.actionId);
      if (existing?.attributes?.signature === item.signature) return;
      if (existing) layer.remove(existing);

      const graphic = new Graphic({
        geometry: item.geometry,
        symbol: polygonSymbolForAction(actionSymbolFacets(item.action, now)),
        attributes: {
          actionId: item.actionId,
          name: formatActionTask(item.action.name, item.action.description),
          signature: item.signature,
        },
      });
      layer.add(graphic);
      graphics.set(item.actionId, graphic);
    });

    graphics.forEach((graphic, actionId) => {
      if (wantedIds.has(actionId)) return;
      layer.remove(graphic);
      graphics.delete(actionId);
    });
  }, [synced]);

  /** Table row selection reads back as a highlight on the map. */
  useEffect(() => {
    const layer = pointLayerRef.current;
    if (!mapView || !layer) return;

    let cancelled = false;

    const apply = async () => {
      const layerView = await mapView.whenLayerView(layer);
      if (cancelled) return;

      highlightRef.current?.remove();
      highlightRef.current = null;

      const objectIds = (selectedActionIds ?? [])
        .map((actionId) => pointEntriesRef.current.get(actionId)?.objectId)
        .filter((objectId): objectId is number => objectId != null);
      if (objectIds.length === 0) return;

      highlightRef.current = (layerView as __esri.FeatureLayerView).highlight(
        objectIds
      );
    };

    void apply();

    return () => {
      cancelled = true;
    };
  }, [mapView, selectedActionIds, synced]);

  const handleActionClick = useCallback(
    async (event: unknown) => {
      if (suppressMapClicks) return;
      const pointLayer = pointLayerRef.current;
      const areaLayer = areaLayerRef.current;
      if (!mapView || (!pointLayer && !areaLayer)) return;

      const include = [pointLayer, areaLayer].filter(
        (layer): layer is FeatureLayer | GraphicsLayer => layer != null
      );
      const hitResult = await mapView.hitTest(event as MouseEvent, { include });

      const clickedGraphicHit = hitResult.results.find(
        (result) =>
          result.type === 'graphic' &&
          (result.graphic.layer?.id === INCIDENT_ACTIONS_LAYER_ID ||
            result.graphic.layer?.id === INCIDENT_ACTION_AREAS_LAYER_ID)
      );

      if (!clickedGraphicHit || clickedGraphicHit.type !== 'graphic') return;

      const actionId = String(
        clickedGraphicHit.graphic.attributes?.actionId || ''
      );
      // Clicking a cluster carries no single action, so let the view zoom in.
      if (!actionId) return;

      const action = actions.find((item) => getActionMapId(item) === actionId);
      if (!action) return;

      onActionGraphicClick?.(actionId);

      const geometry = geometryForAction(action);
      const coords = getActionCoordinates(
        (action as { geolocation?: unknown }).geolocation
      );
      if (!geometry) return;

      const popupLocation = coords
        ? new Point({
            longitude: coords.longitude,
            latitude: coords.latitude,
          })
        : geometry.extent?.center;
      if (!popupLocation) return;

      const popupContent = document.createElement('div');
      const root = createRoot(popupContent);
      popupRootRef.current = root;
      root.render(<ActionPopupContent action={action} />);

      await mapView.goTo(geometry);
      await mapView.openPopup({
        title: formatActionTask(action.name, action.description),
        location: popupLocation,
        content: popupContent,
      });

      const popup = mapView.popup;
      if (!popup) return;

      const handle = popup.watch('visible', (visible) => {
        if (!visible) {
          root.unmount();
          if (popupRootRef.current === root) {
            popupRootRef.current = null;
          }
          handle.remove();
        }
      });
    },
    [actions, mapView, onActionGraphicClick, suppressMapClicks]
  );

  useEffect(() => {
    if (!mapView) return;

    const clickHandle = mapView.on('click', handleActionClick);

    return () => {
      clickHandle.remove();
    };
  }, [mapView, handleActionClick]);
};
